import Heading from "@vfuk/core-heading";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Paragraph from "@vfuk/core-paragraph/dist/Paragraph";
import AuthenticationService from "@services/AuthenticationService";
import { FloatingLabelInput } from "@ucchub/ui";

interface ForgotPasswordFormProps {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  setValidationError: Dispatch<React.SetStateAction<boolean>>;
}

const ForgotPasswordForm = ({
  email,
  setEmail,
  setValidationError,
}: ForgotPasswordFormProps) => {
  const [regEx, setRegEx] = useState<RegExp>(new RegExp(""));
  const [error, setError] = useState("");

  // Focus the Email Input element on initial load.
  const emailFieldRef = useRef<HTMLInputElement>(null);
  useEffect(() => emailFieldRef.current?.focus(), []);

  // Checks the Email against RegEx
  const isEmailValid = (email: string) => regEx.test(email);

  // Used for fetching the Forgot Password RegEx
  const fetchForgotPasswordRegEx = useCallback(async () => {
    const { data } = await AuthenticationService.getForgotPasswordRegEx();

    setRegEx(new RegExp(data[0].validation[0].pattern));
  }, []);

  useEffect(() => {
    // Fetch the RegEx on initial load
    fetchForgotPasswordRegEx();

    // Reset the state when the component unmounts.
    return () => {
      setRegEx(new RegExp(""));
      setEmail("");
      setValidationError(false);
    };
  }, [fetchForgotPasswordRegEx, setEmail, setValidationError]);

  const onEmailChange = (email: string) => {
    // Set the email to the wider AuthBox state.
    setEmail(email);

    // If the field is empty, remove the error but keep the button disabled.
    if (!email) {
      setError("");
      setValidationError(true);
      return;
    }

    // If the field is not empty, but the email is incorrect, show the error.
    if (!isEmailValid(email)) {
      setError("Invalid email address");
      setValidationError(true);
      return;
    }

    // If the field is not empty and the email is correct
    setError("");
    setValidationError(false);
  };

  return (
    <div>
      <Heading size={3}>Forgot password</Heading>
      <Paragraph>
        Please enter your email address to reset your password.
      </Paragraph>

      <FloatingLabelInput
        state={error ? "error" : undefined}
        label="Email"
        error={error}
        type="email"
        value={email}
        id="email"
        onChange={(e) => onEmailChange(e.target.value)}
      />
      <br />
    </div>
  );
};

export default ForgotPasswordForm;
